import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState, type FC, type FormEvent } from 'react';
import { Button, Paper, TextField, Typography } from '@mui/material';
import { OfferApplicantValue } from '@services/offerApplicant/offerApplicant.type';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './translations.json';

import { enqueueSnackbar } from 'notistack';

type SupportedLanguage = keyof typeof translations;

interface IdentificationFormProps {
    offerText: string;
    defaultValue: any;
    onSubmit: (email: string) => void;
    loading: boolean;
}

const IdentificationForm: FC<IdentificationFormProps> = ({ offerText, defaultValue, onSubmit, loading }) => {
    const { language } = useLanguage();
    const content = translations[language as SupportedLanguage] || translations.en;
    const [email, setEmail] = useState('');

    useEffect(() => {
        setEmail(defaultValue?.[OfferApplicantValue.group]?.[OfferApplicantValue.subGroup.information]?.[OfferApplicantValue.field.emailAddress] || '');
    }, [defaultValue]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (!email.trim()) {
            enqueueSnackbar(content.emailError || 'Please enter a valid email address.', { variant: 'error' });
            return;
        }
        event.preventDefault();
        onSubmit(email.trim());
    };

    return (
        <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
                p: { xs: 3, sm: 4 },
                width: '100%',
                maxWidth: 560,
                mx: 'auto',
                color: '#f8fafc',
                backgroundColor: '#121316',
                border: '1px solid #23262d',
                borderRadius: 2,
            }}
        >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#ffffff' }}>
                {content.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                {content.subtitle}
            </Typography>

            <TextField
                fullWidth
                label={content.offer}
                value={offerText}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2, ...fieldStyles }}
            />
            <TextField
                fullWidth
                required
                type="email"
                label={content.email}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                sx={{ mb: 3, ...fieldStyles }}
            />
            <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                    py: 1.2,
                    borderRadius: '8px',
                    backgroundColor: '#1976d2',
                    color: '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#115293' },
                    '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        color: 'rgba(255, 255, 255, 0.3)',
                    }
                }}
            >
                {loading && <CircularProgress size={24} sx={{ color: '#ffffff', mr: 1 }} />}
                {content.continue}
            </Button>
        </Paper>
    );
};

const fieldStyles = {
    '& .MuiInputLabel-root': { color: '#94a3b8' },
    '& .MuiOutlinedInput-root': {
        color: '#ffffff',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '8px',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
        '&:hover fieldset': { borderColor: '#1976d2' },
        '&.Mui-focused fieldset': { borderColor: '#1976d2' },
    },
};

export default IdentificationForm;